import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import AnalyticsStatType from "../../data/analytics-stat-type";
import { MeasurementSystem, VelavuDevice } from "velavu-js-api";
import { classArr } from "../../helper/style-helper";
import * as styles from "./analytics-sidebar-devices-list.module.scss";
import whitelabel from "../../whitelabel";
import LoadingDots from "../../elements/loading-dots";
import AnalyticsSidebarDeviceCard from "./analytics-sidebar-device-card";

export interface AnalyticsSidebarDevicesListProps {
	className?: string;
	style?: CSSProperties;

	devices: VelavuDevice[] | undefined;
	selectedDevices: string[];
	setSelectedDevices: React.Dispatch<React.SetStateAction<string[]>>;

	statType: AnalyticsStatType;
	measurementSystem: MeasurementSystem;
}

export default function AnalyticsSidebarDevicesList(
	props: AnalyticsSidebarDevicesListProps,
) {
	const { t } = useTranslation();

	//Handle loading state
	if (props.devices === undefined) {
		return (
			<div
				className={classArr(
					styles.list,
					styles.loading,
					props.className,
				)}
			>
				<LoadingDots
					size={40}
					color={whitelabel.palette.secondary500}
				/>
			</div>
		);
	}

	//Handle empty state
	if (props.devices.length === 0) {
		return (
			<div
				className={classArr(
					styles.list,
					styles.emptyLabel,
					props.className,
				)}
			>
				{t("common.no_results")}
			</div>
		);
	}

	return (
		<div className={classArr(styles.list, props.className)}>
			{props.devices.map((device) => (
				<AnalyticsSidebarDeviceCard
					className={styles.item}
					key={device.id}
					device={device}
					measurementSystem={props.measurementSystem}
					isSelected={props.selectedDevices.includes(device.id)}
					isNonApplicable={
						!checkStatApplicability(device, props.statType)
					}
					onClick={() => {
						props.setSelectedDevices((selection) => {
							const selectionIndex = selection.indexOf(device.id);
							if (selectionIndex === -1) {
								return selection.concat(device.id);
							} else {
								const newSelection = [...selection];
								newSelection.splice(selectionIndex, 1);
								return newSelection;
							}
						});
					}}
				/>
			))}
		</div>
	);
}

/**
 * Checks if a given statistic can apply to a device
 */
function checkStatApplicability(
	device: VelavuDevice | undefined,
	stat: AnalyticsStatType,
): boolean {
	switch (stat) {
		case AnalyticsStatType.Temperature:
			return device?.environment?.temperature_c !== undefined;
		case AnalyticsStatType.Humidity:
			return device?.environment?.humidity !== undefined;
		case AnalyticsStatType.Battery:
			return device?.state?.power?.battery_level !== undefined;
		case AnalyticsStatType.Location:
			return true;
	}
}
